import { AlertOctagon, AlertTriangle, CheckCircle2, CloudOff, Users, Wrench } from 'lucide-react'

import { cn } from '@/lib/cn'

import type { City, Page, Status } from './api'
import { clock, OVERALL, STATUS } from './meta'

const ICON = {
  operational: CheckCircle2,
  maintenance: Wrench,
  degraded: AlertTriangle,
  partial: AlertTriangle,
  major: AlertOctagon,
} satisfies Record<Status, unknown>

function CityPill({ city }: { city: City | undefined }) {
  if (!city) return null
  const label = !city.online
    ? 'Città spenta'
    : city.players === null
      ? 'Città accesa'
      : `${city.players}${city.max_players ? `/${city.max_players}` : ''} in città`
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 self-start rounded-full border px-3 py-1 text-[12px] font-medium tabular-nums sm:self-auto',
        city.online
          ? 'border-ok/25 bg-ok/10 text-[#b6f5d4]'
          : 'text-mist border-white/10 bg-white/5',
      )}
    >
      <Users className="size-3.5" aria-hidden />
      {label}
    </span>
  )
}

/** The page's answer in one line: is everything working? */
export function Overall({
  page,
  city,
  failed,
}: {
  page: Page | undefined
  city: City | undefined
  failed: boolean
}) {
  if (!page) {
    return (
      <section className="card flex items-center gap-4 p-6 sm:p-8" aria-busy={!failed}>
        {failed ? (
          <>
            <CloudOff className="text-down size-9 shrink-0" aria-hidden />
            <div>
              <p className="font-display text-lg font-semibold">Non riesco a leggere lo stato</p>
              <p className="text-mist mt-1 text-sm">
                Il pannello non risponde: potrebbe essere un problema dei nostri servizi web.
                Riprovo da solo tra poco.
              </p>
            </div>
          </>
        ) : (
          <>
            <span className="size-9 shrink-0 animate-pulse rounded-full bg-white/10" />
            <div className="w-full space-y-2">
              <span className="block h-5 w-2/3 animate-pulse rounded bg-white/10" />
              <span className="block h-3.5 w-1/3 animate-pulse rounded bg-white/5" />
            </div>
          </>
        )}
      </section>
    )
  }

  const look = STATUS[page.overall]
  const Icon = ICON[page.overall]
  return (
    <section
      className="card animate-rise relative overflow-hidden p-6 sm:p-8"
      style={{
        background: `linear-gradient(120deg, color-mix(in srgb, ${look.hex} 22%, #0a1020) 0%, color-mix(in srgb, ${look.hex} 6%, #0a1020) 55%, #0a1020 100%)`,
        borderColor: `color-mix(in srgb, ${look.hex} 35%, transparent)`,
      }}
      aria-live="polite"
    >
      <div
        className="pointer-events-none absolute -top-24 -right-16 size-72 rounded-full opacity-40 blur-3xl"
        style={{ background: look.hex }}
        aria-hidden
      />
      <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <span
            className="grid size-12 shrink-0 place-items-center rounded-2xl"
            style={{ background: `color-mix(in srgb, ${look.hex} 20%, transparent)` }}
          >
            <Icon className={cn('size-7', look.text)} strokeWidth={2.2} aria-hidden />
          </span>
          <div>
            <h1 className="font-display text-[clamp(1.15rem,3.2vw,1.6rem)] leading-tight font-semibold tracking-[-0.02em] text-balance">
              {OVERALL[page.overall]}
            </h1>
            <p className="text-fog/80 mt-1 text-[13px]">
              Aggiornato alle {clock(page.at)} · si aggiorna da solo
              {failed && <span className="text-slow"> · ultimo tentativo non riuscito</span>}
            </p>
          </div>
        </div>
        <CityPill city={city} />
      </div>
    </section>
  )
}
